import { suppressGuard } from '../guards/unsavedChanges';
import { runStepUpCeremony } from './stepUp';
import type { StepUpTier, StepUpReason, StepUpAction, ConflictReason, RateLimitReason } from './types';

const BASE_URL = import.meta.env.VITE_API_BASE_URL;

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    /** Present only on a Stream 4 `403 STEP_UP_REQUIRED` — which gate tripped
     *  (`view` | `reveal`). Absent for every other error. */
    public readonly tier?: StepUpTier,
    /**
     * Why the call was refused, in whichever vocabulary the status carries.
     *
     * On a `403 STEP_UP_REQUIRED` it is a {@link StepUpReason}: whether the whole workspace
     * is floored or a single operation is gated, which decides whether the prompt may be
     * dismissed. On a `409 CONFLICT` it is a {@link ConflictReason}, and on a
     * `429 INVITE_DAILY_LIMIT` it is a {@link RateLimitReason} (steward-app#2051), because the
     * façade's `errorToResponse` puts all three in the same key.
     *
     * ⚠️ **Read `status` before you read this.** The three vocabularies do not overlap and
     * nothing here tells them apart. It used to be typed as `StepUpReason` alone, which was
     * simply wrong about what arrives on a 409 (steward-app#810).
     */
    public readonly reason?: StepUpReason | ConflictReason | RateLimitReason,
    /** Present only on a `429 RATE_LIMITED` (F32 invite throttling) — the ISO instant
     *  the call becomes allowed again. Absolute rather than the `Retry-After` header's
     *  integer seconds, so a countdown built from it stays correct across a slow
     *  response and a re-render. Absent on every other error. */
    public readonly retryAt?: string,
    /** Present only on a `409 CONNECTION_PRICE_CHANGED` (steward-app#2231): the price of the
     *  next connection as the façade computes it now. Unvalidated JSON; read it through
     *  `parseQuote` in `lib/connectionOverage.ts`, never directly. */
    public readonly currentQuote?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * PS4 — mid-session workspace suspension. A workspace can go from active to
 * suspended (or archived / soft-deleted) *while the user is already inside the
 * app*: trial expiry (the trial-expiry sweeper) or a billing lapse (the Stripe
 * webhook), both async and independent of anything the SPA is doing. Every
 * workspace-scoped call made after that lands a 403 from appcore's workspace
 * middleware, which previously read to the caller as an empty/broken page — the
 * "Paused" explanation only existed on the pre-session /select-workspace picker.
 *
 * The discriminator: appcore's workspace middleware (packages/core/src/middleware/
 * workspace.ts, buildRequestContext) tags exactly the three "workspace isn't
 * active" throws with this explicit code; it is forwarded verbatim through the
 * façade's UpstreamError (services/steward-app-api/src/appcore-client.ts,
 * middleware.ts) into this ApiError's `code`. An ordinary permission-denied 403
 * (missing membership, inactive membership, a capability/ACL check elsewhere in
 * the API) keeps the generic `FORBIDDEN` code and must NOT trigger this — those
 * are legitimate authorization failures the caller needs to see, not a
 * workspace-level access loss.
 */
export const WORKSPACE_SUSPENDED_CODE = 'WORKSPACE_SUSPENDED';

/** An {@link ApiError}'s `reason` read as a step-up reason, or `undefined` when it is one of
 *  the other vocabularies that share that field. */
function stepUpReason(reason: string | undefined): StepUpReason | undefined {
  return reason === 'floor' || reason === 'operation' ? reason : undefined;
}

/** True when a failed response's status + error code identify a mid-session
 *  workspace suspension, as opposed to an ordinary authorization 403. Pure and
 *  independent of the DOM so it's unit-testable without a browser environment. */
export function isWorkspaceSuspendedResponse(status: number, code: string | undefined): boolean {
  return status === 403 && code === WORKSPACE_SUSPENDED_CODE;
}

/**
 * appcore's optimistic-concurrency rejection, forwarded verbatim by the façade. Status
 * and code are both accepted for the same reason NewWorkspaceDialog's own inline check
 * accepts both: the code comes from appcore's `transactWrite` wrapper and a 409 from
 * this route can mean nothing else.
 *
 * Moved here from `AnnotationsPanel.tsx` (steward-app#526), which is where it originated
 * and still uses it. Its second caller is `saveAnnotations` on each of the four detail
 * pages — a plain function, not a component, so `AnnotationsPanel.tsx` stopped being a
 * home it could live in unmoved. `client.ts` already collects this shape of predicate
 * (see `isWorkspaceSuspendedResponse` just above), so it is the natural shared home
 * rather than a copy in a fifth place. `NewWorkspaceDialog`'s inline version is left as
 * is: consolidating it is out of scope for #526.
 */
export function isVersionConflict(err: unknown): boolean {
  return err instanceof ApiError && (err.status === 409 || err.code === 'TRANSACTION_CANCELLED');
}

// window.location.assign() starts a navigation but doesn't synchronously change
// window.location.pathname — several workspace-scoped queries can fail in the
// same tick (e.g. the Dashboard's parallel account/asset/liability/entity
// fetches all 403 together) and each would otherwise re-fire the redirect
// before the browser actually leaves the page. Latches after the first call so
// only one redirect (and one suppressGuard) happens per page lifetime.
let redirecting = false;

/**
 * Bounces the user to /select-workspace, which already renders the "Paused"
 * explanation and (for a workspace admin) the Manage billing CTA — see
 * suspendedReason() in pages/SelectWorkspace.tsx. A full page navigation
 * (window.location.assign), not react-router's navigate: this module has no
 * component/router context to call it from, and this mirrors the codebase's
 * existing escape hatch for a forced redirect from outside React (forced
 * sign-out — see guards/unsavedChanges.ts). suppressGuard() is called first so
 * an unsaved-changes prompt never blocks it, for the same reason a forced
 * sign-out suppresses it: there's no "save" available for a workspace access
 * has just been lost to. Guarded against a redirect loop: a no-op if already on
 * /select-workspace, or if a redirect is already underway.
 *
 * That pathname guard is load-bearing, not merely belt-and-braces for a stray
 * double-fire. /select-workspace renders inside Layout and IS reachable with a
 * healthy active workspace (WorkspaceSwitcher navigates there directly for a
 * co-existing suspended membership, without calling switchWorkspace), so
 * workspace-scoped calls genuinely do fire from that page — Layout mounts
 * GET /feedback, for one. A 403 from any of them lands right back here.
 */
function redirectToSelectWorkspace(): void {
  if (typeof window === 'undefined') return;
  if (redirecting) return;
  if (window.location.pathname === '/select-workspace') return;
  redirecting = true;
  suppressGuard();
  window.location.assign('/select-workspace');
}

/**
 * The active workspace (O3 multi-workspace). Set by WorkspaceProvider and read
 * ambiently here, so the ~50 `createApiClient(token)` call sites don't each have
 * to thread a workspace id. Every request carries it as `X-Workspace-Id`; the
 * façade forwards it to appcore, which validates the caller's membership. When
 * unset (single-workspace deploys), the façade falls back to its deploy default.
 */
let activeWorkspaceId: string | null = null;
export function setActiveWorkspaceId(id: string | null) { activeWorkspaceId = id; }
export function getActiveWorkspaceId(): string | null { return activeWorkspaceId; }

/**
 * The workspace ONE call is bound to. Both fields are resolved together by
 * {@link bindWorkspace} when the call is first built, and then carried unchanged
 * through every attempt of that call — nothing in here is ever re-read from module
 * state again.
 */
interface WorkspaceBinding {
  /** Whether the CALLER named a workspace for this call, as opposed to inheriting the
   *  ambient active one. Decides the suspension bounce — see the 403 branch below. */
  readonly isOverridden: boolean;
  /** The id that goes on the wire as `X-Workspace-Id`. `null` sends no header, leaving
   *  the façade to fall back to its deploy default (single-workspace deploys). */
  readonly workspaceId: string | null;
  /** The caller's explicit opt-in to the in-context prompt for a `floor` refusal on a
   *  foreign workspace (steward-app#1529). See {@link ForeignFloorPresentation}. */
  readonly foreignFloor?: ForeignFloorPresentation;
}

/**
 * The ONLY value of `ScopedGetOptions.foreignFloor`, and the only way a `floor` step-up
 * refusal is ever shown as the dismissible in-context prompt (steward-app#1529).
 *
 * A named string rather than a boolean so it cannot be set by accident or by a default:
 * a call site has to spell it out, and `grep foreignFloor` finds every one. Opting in is
 * a claim about the PAGE, which no amount of inspecting the request can make: that this
 * read is one item among several on a page the user still holds, so cancelling costs
 * them that one item. Inferring it from "the read names another workspace" was the
 * first version, and it was too broad: a firm-scoped `/advisor/*` page opened while the
 * advisor's own household is active names the firm on EVERY read, and a dismissible
 * prompt there dead-ends the whole page.
 */
export type ForeignFloorPresentation = 'in-context';

/**
 * Resolves the workspace a call is bound to, ONCE, at the moment the call is made.
 *
 * `overrideWorkspaceId` is the per-request `X-Workspace-Id` override (the advisor
 * portal, docs/25 §2/S7, reads a client workspace's net worth and connection record
 * without switching the SPA's active workspace). `undefined` means "no override, use
 * the ambient active workspace" — every pre-existing call site passes nothing and is
 * unaffected.
 *
 * Resolving the ID here rather than at each `rawRequest` is the point, and it is a
 * safety property, not a tidy-up (steward-app#869). `activeWorkspaceId` is module
 * state that WorkspaceProvider rewrites whenever the active workspace changes, and a
 * step-up 403 makes a call OUTLIVE that: `request` below pauses on the ceremony and
 * then replays the SAME body. If the replay re-read `activeWorkspaceId` it could go
 * out under a different family's `X-Workspace-Id` than the one the caller composed the
 * body for — and for the no-foreign-id POSTs (`/credentials`, `/invites`, `/contacts`,
 * `/feedback`, `/vaults`, `/connections`) the server has nothing to refuse it with,
 * because the new workspace really is one the user belongs to. Binding once means a
 * replay either reaches the workspace it was authorised under, or fails there.
 */
function bindWorkspace(overrideWorkspaceId?: string, foreignFloor?: ForeignFloorPresentation): WorkspaceBinding {
  return overrideWorkspaceId !== undefined
    ? { isOverridden: true, workspaceId: overrideWorkspaceId, foreignFloor }
    // `foreignFloor` is carried on BOTH branches on purpose: an opt-in without an override
    // must be refused by `stepUpPresentationReason`'s own `isOverridden` guard, not
    // silently dropped here, so that guard stays load-bearing and tested on its own.
    : { isOverridden: false, workspaceId: activeWorkspaceId, foreignFloor };
}

/**
 * The presentation reason for a step-up 403 on a call bound to `workspace`
 * (steward-app#1529). It is the server's own reason, with exactly one rewrite: a
 * `'floor'` refusal is presented as `'operation'`, the dismissible in-context prompt,
 * only when the caller OPTED IN (`foreignFloor: 'in-context'`) AND the call named a
 * workspace other than the active one.
 *
 * Why that is safe. The session-entry wall exists so the ACTIVE session cannot proceed
 * under its floor; `AssuranceFloorGate` owns that case. The one opted-in caller, an
 * advisor's client-roster card reading a client's net worth, does not move the session
 * anywhere: the floor refused that read, the figure was never fetched, and cancelling
 * fails only the reads that shared that prompt (every card coalesced into it, plus any
 * other request that joined). Without the rewrite the advisor's own working page got the
 * non-dismissible wall, whose only exits are another workspace or sign-out.
 * `AdvisorOverview`'s VerifyPrompt passes `'operation'` for the same reason.
 *
 * Why an opt-in rather than inferring it from the override: see
 * {@link ForeignFloorPresentation}. Every other override (every firm-scoped `/advisor/*`
 * read included) gets the wall exactly as before #1529.
 *
 * Fail closed: the opt-in is necessary, never sufficient. The rewrite also needs an
 * explicit override, a non-empty bound id, a non-null active id, and the two differing.
 * Anything unknown keeps `'floor'`, so a call that inherited the ambient workspace (no
 * override), or that sends no header (`null` or `''`, which the façade resolves to its
 * deploy default), is never made dismissible, opted in or not.
 *
 * `activeWorkspaceId` is read HERE, when the 403 lands, rather than captured in the
 * binding: the question is whether the wall would stand over the session that is active
 * NOW. If the advisor switched INTO the bound workspace while the read was in flight,
 * the two now match and the wall stays. The remaining window: until `/me` resolves (on
 * sign-in AND on every reload) the active id is the optimistic stored pick, so if that
 * pick turns out stale, an opted-in read naming the workspace that WILL be active
 * compares as foreign. The roster card renders from `/me`'s memberships, so it cannot
 * fire before `/me` resolves; and a floor on the active workspace raised by any ambient
 * read is never rewritten, and escalates a shared prompt (`runStepUpCeremony`'s header).
 */
function stepUpPresentationReason(reason: StepUpReason | undefined, workspace: WorkspaceBinding): StepUpReason | undefined {
  if (reason !== 'floor') return reason;
  const foreign = workspace.foreignFloor === 'in-context'
    && workspace.isOverridden
    && !!workspace.workspaceId
    && !!activeWorkspaceId
    && workspace.workspaceId !== activeWorkspaceId;
  return foreign ? 'operation' : reason;
}

/**
 * Wording only: whether the gated call is the person SAVING something (a POST or PUT at the
 * reveal tier: an account number, a tax id, a credential) rather than revealing it. The dialog
 * would otherwise say "revealing this" over a Save button. A DELETE at the reveal tier is `'remove'`. Reads (GET) stay on the
 * default, and the call they make to the ceremony keeps its three arguments.
 */
export function stepUpAction(tier: StepUpTier | undefined, method: string | undefined): StepUpAction | undefined {
  const m = (method ?? 'GET').toUpperCase();
  if (tier !== 'reveal') return undefined;
  if (m === 'POST' || m === 'PUT') return 'save';
  if (m === 'DELETE') return 'remove';
  return undefined;
}

// Bounds every request so a stalled connection fails fast into React Query's
// retry (queryClient defaultOptions.retry) instead of leaving the UI stuck on
// an isLoading spinner forever — a fetch() with no signal never settles on a
// hung socket.
const REQUEST_TIMEOUT_MS = 15_000;

/**
 * `AbortSignal.any`, with a hand-rolled fallback: the built-in is missing on Safari/iOS before
 * 17.4, Chrome before 116 and Firefox before 124, and the app declares no browser floor. The
 * result aborts with the reason of whichever source fires first (a `TimeoutError` for the
 * timeout, an `AbortError` for a caller's abort), which is what `isConnectivityError` reads.
 * The fallback removes its listeners once either side has fired.
 */
function anySignal(signals: AbortSignal[]): AbortSignal {
  if (typeof AbortSignal.any === 'function') return AbortSignal.any(signals);
  const controller = new AbortController();
  const listeners: Array<[AbortSignal, () => void]> = [];
  const cleanup = () => { for (const [sig, fn] of listeners) sig.removeEventListener('abort', fn); };
  for (const sig of signals) {
    if (sig.aborted) { controller.abort(sig.reason); cleanup(); return controller.signal; }
    const fn = () => { controller.abort(sig.reason); cleanup(); };
    sig.addEventListener('abort', fn, { once: true });
    listeners.push([sig, fn]);
  }
  return controller.signal;
}

async function rawRequest<T>(
  path: string,
  token: string,
  init: RequestInit = {},
  // Which workspace this call is bound to — see {@link bindWorkspace}. REQUIRED, with
  // no default, on purpose: the whole point is that the binding is made once by the
  // caller and then handed to every attempt, so a call site that forgot to pass one
  // must be a compile error rather than a silent re-read of module state.
  workspace: WorkspaceBinding,
  // A per-call figure for the rare route the server is allowed to hold open longer than the
  // default (see {@link PostOptions}). Read on every attempt, step-up replay included. Anything
  // but a positive finite number is replaced by the default below.
  timeoutMs: number = REQUEST_TIMEOUT_MS,
  // Streaming (docs/41): resolve with the live `Response` instead of its parsed body, and bound
  // ONLY the wait for the response headers by `timeoutMs`. `AbortSignal.timeout` would keep
  // running through the body and cut a healthy stream off at that figure, so the timer is
  // manual and is cleared the moment the headers arrive. The caller owns the body from there
  // (its own watchdog, its caller's signal). A refusal still throws an `ApiError` as usual.
  streaming = false,
): Promise<T> {
  const ms = Number.isFinite(timeoutMs) && timeoutMs > 0 ? timeoutMs : REQUEST_TIMEOUT_MS;
  let clearTimer = () => {};
  let timeout: AbortSignal;
  if (streaming) {
    const timer = new AbortController();
    const t = setTimeout(() => timer.abort(new DOMException('The operation timed out.', 'TimeoutError')), ms);
    clearTimer = () => clearTimeout(t);
    timeout = timer.signal;
  } else {
    timeout = AbortSignal.timeout(ms);
  }
  // Both come from the binding, so NEITHER is re-read from module state here — not on
  // the first attempt and not on a step-up replay. See the 403 branch below for why
  // `isOverridden` must be fixed that way, and `bindWorkspace` for why the id must.
  const { isOverridden, workspaceId: effectiveWorkspaceId } = workspace;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
    ...(effectiveWorkspaceId ? { 'X-Workspace-Id': effectiveWorkspaceId } : {}),
  };
  let res: Response;
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      ...init,
      // A caller's own signal (only `post()` can supply one, see {@link PostOptions}) is COMBINED
      // with the timeout rather than replacing it, so a Stop and a stalled socket both end the call.
      signal: init.signal ? anySignal([init.signal, timeout]) : timeout,
      headers,
    });
  } catch (err) {
    clearTimer();
    throw err;
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ code: 'UNKNOWN', message: res.statusText })) as { code?: string; message?: string; tier?: StepUpTier; reason?: StepUpReason | ConflictReason | RateLimitReason; retryAt?: string; currentQuote?: unknown };
    // The global "workspace suspended" bounce (redirectToSelectWorkspace, above) only
    // makes sense when the 403 is about the workspace the SPA currently treats as
    // active — historically the only case, since every request implicitly targeted
    // it. The advisor portal's per-request override breaks that assumption: an
    // advisor reading several CLIENTS' net worth can legitimately hit one that is
    // suspended for billing while the advisor's own active workspace (the firm's) is
    // perfectly healthy. Without this guard, that one 403 would evict the advisor
    // from their own session to /select-workspace over a workspace that isn't theirs
    // and isn't active.
    //
    // The guard is `!isOverridden`, a boolean CAPTURED AT REQUEST-BUILD TIME (in the
    // binding) — deliberately not a comparison of two ids re-read here, at response
    // time. An earlier version compared `effectiveWorkspaceId` against the live
    // `activeWorkspaceId` module variable when the response arrived, which is a
    // time-of-check/time-of-use gap: `activeWorkspaceId` can change while the request
    // is in flight (most concretely, at startup — an ambient query fires with it still
    // `null` before WorkspaceProvider's `/me` resolves and sets it; if that ambient
    // request's workspace turns out to be suspended, the two reads would disagree by
    // the time the 403 lands, and the redirect a genuinely-suspended default workspace
    // needs would silently not fire). Whether THIS call carries an override is fixed
    // the moment the call is made and cannot change underneath it, so branching on
    // that instead is correct regardless of how many times `activeWorkspaceId` changes
    // before the response arrives. See client.test.ts for both regression cases.
    //
    // The id in the binding is fixed for exactly the same reason, one step further out:
    // it survives not just the wait for THIS response but the step-up ceremony and the
    // replay that follows it (steward-app#869, and `bindWorkspace`'s header).
    if (isWorkspaceSuspendedResponse(res.status, body.code) && !isOverridden) {
      redirectToSelectWorkspace();
    }
    clearTimer();
    throw new ApiError(res.status, body.code ?? 'UNKNOWN', body.message ?? res.statusText, body.tier, body.reason, body.retryAt, body.currentQuote);
  }

  if (streaming) {
    clearTimer();
    return res as unknown as T;
  }

  // A 204, or any empty body (the façade's json() helper serializes a null
  // body to an empty string — e.g. /net-worth when no snapshot exists yet),
  // has no JSON to parse: res.json() would throw "Unexpected end of JSON
  // input". Resolve empty to null, NOT undefined: React Query rejects a
  // queryFn that resolves to undefined ("Query data cannot be undefined") and
  // marks the query errored, which is exactly the failure this guards against.
  // null is a valid query result and a valid JSON value; every 204/delete
  // caller ignores the return, so null is safe for them too.
  const text = await res.text();
  return (text ? JSON.parse(text) : null) as T;
}

/** A 403 whose code identifies a Stream 4 step-up gate (appcore docs/28 §9) —
 *  as opposed to an ordinary permission-denied 403, which must pass straight
 *  through untouched (same as every 401 always does; a 401 means the session
 *  itself is dead, never routed through this).
 *
 *  Exported for the same reason {@link WORKSPACE_SUSPENDED_CODE} is: a surface that
 *  renders its own copy for a failed read has to tell these two 403s apart, and
 *  "no longer available to you" is simply false about a step-up refusal
 *  (`AdvisorClients.tsx`). Reading the code is the ONLY thing a caller may do with
 *  it — the interceptor below owns every decision about the ceremony itself. */
export const STEP_UP_REQUIRED_CODE = 'STEP_UP_REQUIRED';

// One retry after a successful ceremony, plus one more to cover a second,
// stricter-tier gate tripping right after (a `reveal` prompt appearing on the
// heels of a `view` one — see stepUp.ts). A THIRD consecutive STEP_UP_REQUIRED
// on the same call is treated as a real failure (a broken/rejected ceremony),
// not something to keep retrying forever.
const MAX_STEP_UP_ATTEMPTS = 2;

/**
 * The step-up interceptor. Wraps every request: on an ordinary response (or
 * any error other than a step-up gate) it behaves exactly like `rawRequest`.
 * On `403 STEP_UP_REQUIRED` it pauses, runs (or joins) the step-up ceremony
 * via {@link runStepUpCeremony} — which coalesces concurrent 403s into a
 * single prompt — and then transparently retries the ORIGINAL request, against
 * the workspace that request was bound to when it was first made. The
 * caller never sees the 403 unless the ceremony itself fails/is cancelled, or
 * the retried call keeps tripping the gate past `MAX_STEP_UP_ATTEMPTS`.
 */
async function request<T>(
  path: string, token: string, init: RequestInit = {}, workspace: WorkspaceBinding, attempt = 0,
  timeoutMs: number = REQUEST_TIMEOUT_MS,
  streaming = false,
): Promise<T> {
  try {
    return await rawRequest<T>(path, token, init, workspace, timeoutMs, streaming);
  } catch (err) {
    if (err instanceof ApiError && err.status === 403 && err.code === STEP_UP_REQUIRED_CODE && attempt < MAX_STEP_UP_ATTEMPTS) {
      // Narrowed rather than passed through: `reason` carries two vocabularies (see
      // `ApiError.reason`), and the ceremony only understands one of them. Anything else
      // becomes `undefined`, which that function's own header calls the safe default. A
      // floor on a workspace other than the active one is presented in context
      // (steward-app#1529, `stepUpPresentationReason`).
      const action = stepUpAction(err.tier, init.method);
      const presentation = stepUpPresentationReason(stepUpReason(err.reason), workspace);
      await (action ? runStepUpCeremony(token, err.tier, presentation, action) : runStepUpCeremony(token, err.tier, presentation));
      // `workspace` is threaded through rather than re-derived: the ceremony can take
      // seconds (a TOTP code typed in, a native passkey prompt), and `activeWorkspaceId`
      // is module state that can change during it from anything the modal does not
      // block — most concretely WorkspaceProvider re-resolving the active workspace when
      // its background `/me` lands. Re-reading it here would replay the original body
      // under whatever workspace is active by then (steward-app#869).
      return request<T>(path, token, init, workspace, attempt + 1, timeoutMs, streaming);
    }
    throw err;
  }
}

// True when the failure happened before a server ever replied: the browser's
// own fetch rejection (offline, DNS, CORS - a bare TypeError) or our
// REQUEST_TIMEOUT_MS abort (a DOMException named AbortError or TimeoutError).
// Those throw terse, technical text ("Failed to fetch", "The operation was
// aborted") that reads like a bug report, not something a user should see.
export function isConnectivityError(err: unknown): boolean {
  if (err instanceof TypeError) return true;
  if (err instanceof DOMException) return err.name === 'AbortError' || err.name === 'TimeoutError';
  return false;
}

// Server error codes that need friendlier copy than the raw ApiError message
// they arrive with. Centralized here (rather than re-derived per call site)
// so every caller of userMessage — including ConfirmModal's own internal
// catch, which callers can't intercept — gets the same wording. LAST_ADMIN is
// the workspace-members "can't demote/remove the only admin" guard (409); it
// used to be special-cased only in people/Access.tsx for the role-change
// path, but PS3 needed it for the (now rethrowing) remove-member confirm flow
// too, so it moved here.
/** appcore#774: an advisor invite naming a firm whose connection with the family has ENDED is
 *  refused on accept (409 `INVITE_ENGAGEMENT_ENDED`). ONE string for both accept surfaces
 *  (SelectWorkspace, via `FRIENDLY_ERROR_CODE`'s own 409 ordering, and WaitingList, via
 *  `userMessage`). It promises no outcome: the invite may expire, or a reconnect may issue a
 *  new invite that supersedes this one. */
export const INVITE_ENGAGEMENT_ENDED_MESSAGE =
  'This invitation came from a family whose connection with your firm has ended, so it cannot be accepted. If the family reconnects with your firm, you will be able to join then.';

const FRIENDLY_ERROR_CODE: Record<string, string> = {
  INVITE_ENGAGEMENT_ENDED: INVITE_ENGAGEMENT_ENDED_MESSAGE,
  LAST_ADMIN: 'This is the only admin. Make another person an admin first.',
  // Stream 4 — appcore's last-usable-factor guard on DELETE /me/factors/{id}
  // (never let a user remove their only way to prove it's them).
  LAST_FACTOR: 'This is your last sign-in key. Add another before removing this one, so you keep access to your account.',
  // The optimistic-concurrency rejection: `expectedVersion`, frozen at open (steward-app#458,
  // see portfolio/forms.tsx), no longer matches the record's stored version. appcore's
  // `transactWrite` wraps the DynamoDB failure verbatim (`packages/core/src/db/transact.ts`),
  // so without an entry here the family reads "Transaction cancelled: Transaction cancelled,
  // please refer cause-related error messages [ConditionalCheckFailed, None, None]" — storage
  // internals, and no hint of what happened.
  //
  // steward-app#527: a 409 proves only that the version moved, not that a PERSON moved it
  // (refetchOnWindowFocus across two tabs, a sibling form on the page saving first, and a
  // background sync all reach it with no co-editor involved), so this used to say "Someone
  // else changed this" on no evidence and sent every reader off to ask a family member who
  // changed nothing. It also used to instruct "close this form and open it again" — sound
  // advice on the two surfaces this string reaches through a form (DocumentDetail's editor,
  // ContactForm), but Documents.tsx's drag-and-drop move and Access.tsx's role-change select
  // also route their 409 through this same string with no form to close at all, and this is
  // shared precisely because it cannot know which caller it is talking to. So it says only
  // what every caller can stand behind: the record changed, the save did not happen, nothing
  // typed was lost. Matches the shape #473 landed on AnnotationsPanel for the same reason.
  TRANSACTION_CANCELLED: 'This record changed after you opened it, so your save did not go through. Nothing you typed is lost.',
  // appcore#567 — the malware-scan gate on document downloads. Both codes are forwarded
  // verbatim from appcore through the façade's UpstreamError (appcore-client.ts extracts
  // `code` and status; middleware.ts re-emits them), so the façade needed no change and
  // this table is the only place that needs to know the two apart.
  //
  // Scanning is asynchronous, so a 409 DOCUMENT_SCAN_PENDING is the ordinary state for the
  // few moments right after a family uploads a file and immediately clicks it: the file is
  // almost certainly fine and the download is retryable. A 403 DOCUMENT_BLOCKED means the
  // scan finished and the file is NOT clean; it is terminal, and retrying cannot help.
  // Collapsing these into one message would tell a family their own clean file was
  // "blocked by a security check" when it is merely still being scanned, alarming and
  // wrong, so the two codes must keep separate copy here, never a shared fallback.
  DOCUMENT_SCAN_PENDING: 'This file is still being checked. Try again in a moment.',
  DOCUMENT_BLOCKED: 'This file was blocked by a security check.',
  // The Prospects Dismiss (Matt, 2026-11-02): appcore admits only holders of `steward:write`
  // (`steward-admin`, `steward-member`), so a firm owner with no Steward role is refused. The
  // button stays (the old Dismiss had no gate either); the confirm says why it did not work.
  PROSPECT_DISMISS_NOT_AUTHORIZED: 'Only firm staff with a Steward role can dismiss a prospect.',
  // crossmarc/steward-app#2737: the firm's answers read is limited per person (appcore, 120 per 5
  // minutes). The façade relays that 429 under this code with this same sentence
  // (`CLIENT_ANSWERS_BUSY_MESSAGE`, clientAnswersFirm.ts); the entry is here so a surface that
  // reads the code, not the message, says the same thing.
  CLIENT_ANSWERS_BUSY: 'Client answers were opened many times in the last few minutes. Please wait a few minutes and try again.',
};

/** The curated copy `userMessage` would use for this error code, or undefined. Own-property
 *  only, so a code of `constructor` or `toString` never returns an inherited function. For
 *  default-deny callers (`lib/curatedError.ts`) that want this table without `userMessage`'s
 *  raw-`message` passthrough. */
export function friendlyErrorCopy(code: string): string | undefined {
  return Object.hasOwn(FRIENDLY_ERROR_CODE, code) ? FRIENDLY_ERROR_CODE[code] : undefined;
}

/**
 * Turns a caught error into copy safe to show a user. Connectivity failures
 * (offline, timeout) get a warm, generic message instead of the raw browser
 * string; a handful of known ApiError codes (FRIENDLY_ERROR_CODE) get copy
 * friendlier than the server's raw message; any other Error's message passes
 * through as-is (server-provided ApiError messages, plus deliberate
 * `new Error(...)` copy thrown by our own code, e.g. download.ts); non-Error
 * throws fall back to the caller-supplied default. The connectivity check
 * runs first because those failures are also `instanceof Error` — letting
 * them through would leak "Failed to fetch".
 */
export function userMessage(err: unknown, fallback: string): string {
  if (isConnectivityError(err)) return "We couldn't reach Steward. Check your connection and try again.";
  if (err instanceof ApiError && FRIENDLY_ERROR_CODE[err.code]) return FRIENDLY_ERROR_CODE[err.code];
  if (err instanceof Error) return err.message;
  return fallback;
}

/**
 * `get()`'s only optional extra: a per-request `X-Workspace-Id` override (the
 * advisor portal, docs/25 §2/S7). Deliberately NOT `RequestInit` — a wider type
 * would let a caller smuggle a `method`, `body`, or `signal` through what looks
 * like a read, and `rawRequest` would honor every one of them (`method`/`body`
 * would replay as a mutation on React Query's idempotent-GET retry/refetch
 * machinery; `signal` would silently override the built-in `REQUEST_TIMEOUT_MS`
 * abort). This shape makes all three a type error instead of a documented hope.
 */
export interface ScopedGetOptions {
  /** Read a DIFFERENT workspace than the SPA's active one, for this call only.
   *  Convenience only — appcore still membership-checks the request against
   *  whichever workspace this resolves to; see `rawRequest`'s header comment. */
  workspaceId?: string;
  /** Opt this read in to the dismissible prompt when a `floor` refuses it on a workspace
   *  other than the active one. Read {@link ForeignFloorPresentation} before using it; the
   *  roster card in `AdvisorClients.tsx` is the one caller. Ignored without `workspaceId`,
   *  and ignored on the raw client, which never raises a ceremony. */
  foreignFloor?: ForeignFloorPresentation;
}

/**
 * `put()`'s equivalent (steward-app#740). Deliberately the SAME one-field shape rather
 * than a wider one, for the reason spelled out on {@link ScopedGetOptions}: a
 * `RequestInit` here would let a caller override `signal` and lose the built-in timeout.
 *
 * The advisor portal is the only caller. It reads the firm's intake submissions with an
 * explicit `workspaceId` because the advisor may have opened a CLIENT workspace and never
 * switched back (docs/25 §2/S7) — and a status write has to land in the same workspace
 * the row was read from, not in whatever is ambient. Without this the write would be
 * aimed by SPA state that the page does not control.
 *
 * It grants nothing: appcore membership-checks the request against whichever workspace
 * this resolves to, exactly as it does on a read.
 */
export type ScopedWriteOptions = Pick<ScopedGetOptions, 'workspaceId'>;

/**
 * `post()`'s only optional extra: how long THIS call may take before the browser gives up.
 * The default ({@link REQUEST_TIMEOUT_MS}, 15s) applies when it is absent, and also when the
 * figure is not a positive finite number (0, a negative, NaN, Infinity). A route whose server
 * legitimately waits longer than that (the kept-bank move, which waits up to about 24s for its
 * worker) passes a larger figure so the browser does not report a failure for a call the
 * server is still about to finish.
 *
 * `signal` lets the caller abandon THIS call (the assistant's Stop control). The earlier
 * reason for keeping a signal out of these option types was that `fetch` would honour it
 * INSTEAD of the timeout; `rawRequest` now combines the two with `AbortSignal.any`, so the
 * built-in timeout still applies. It is offered on `post()` only: `get()` and `put()` keep
 * their narrower shapes ({@link ScopedGetOptions}). Aborting stops the BROWSER waiting; the
 * server has no way to know and finishes the work.
 */
export interface PostOptions { timeoutMs?: number; signal?: AbortSignal }

/**
 * Factory: call once per component with the token from useAccessToken().
 *
 * Writes deliberately go through here as plain awaited calls rather than
 * React Query mutations, and that is load-bearing for offline behavior:
 * `fetch()` rejects immediately when there is no network, so a save fails fast
 * and the caller's catch shows userMessage()'s "We couldn't reach Steward"
 * copy. A `useMutation` would instead be *paused* by the default
 * `networkMode: 'online'` — its promise simply never settles while offline, so
 * any `setBusy(true)` around it would leave a "Saving…" button spinning with
 * no error and no timeout, which is precisely the dead-end this codebase avoids.
 *
 * If mutations are ever migrated to useMutation, set `networkMode: 'always'`
 * on them (or handle `isPaused` in every form) before doing so. The read side
 * relies on the opposite default on purpose — see src/lib/queryState.ts.
 */
export function createApiClient(token: string) {
  return {
    // Every method binds its workspace here, at the moment the caller invokes it — one
    // visible `bindWorkspace(...)` per call site, none of them implicit.
    get:    <T>(path: string, opts?: ScopedGetOptions) => request<T>(path, token, {}, bindWorkspace(opts?.workspaceId, opts?.foreignFloor)),
    post:   <T>(path: string, body: unknown, opts?: PostOptions) =>
      request<T>(path, token, { method: 'POST', body: JSON.stringify(body), ...(opts?.signal ? { signal: opts.signal } : {}) }, bindWorkspace(), 0, opts?.timeoutMs),
    /** POST that resolves with the live `Response` for an NDJSON stream (docs/41). `timeoutMs`
     *  bounds the wait for the headers only. Used by the assistant's streaming ask. */
    postStream: (path: string, body: unknown, opts?: PostOptions) =>
      request<Response>(path, token, { method: 'POST', body: JSON.stringify(body), ...(opts?.signal ? { signal: opts.signal } : {}) }, bindWorkspace(), 0, opts?.timeoutMs, true),
    put:    <T>(path: string, body: unknown, opts?: ScopedWriteOptions) =>
      request<T>(path, token, { method: 'PUT', body: JSON.stringify(body) }, bindWorkspace(opts?.workspaceId)),
    delete: <T>(path: string, opts?: ScopedWriteOptions) => request<T>(path, token, { method: 'DELETE' }, bindWorkspace(opts?.workspaceId)),
    /** PATCH, for a partial write (the staff answers patch, crossmarc/steward-app#2538). */
    patch:  <T>(path: string, body: unknown, opts?: ScopedWriteOptions) =>
      request<T>(path, token, { method: 'PATCH', body: JSON.stringify(body) }, bindWorkspace(opts?.workspaceId)),
  };
}

/**
 * Bypasses the step-up interceptor: a `403 STEP_UP_REQUIRED` comes back as an {@link ApiError}
 * instead of opening a ceremony. Four kinds of caller, each for its own reason:
 *
 *   - the step-up ceremony's own calls to `/me/stepup/*`, made through api/mfa.ts
 *     from four files: src/mfa/StepUpProvider.tsx, the snapshot form's own inline prompt
 *     (src/intake/IntakeSecondCheckPrompt.tsx: the form draws no full-window layer and no
 *     link into the app), and the verify steps of src/onboarding/OnboardingFlow.tsx and
 *     src/pages/settings/LinkLoginModal.tsx. `src/api/stepUpCallers.test.ts` pins that
 *     these four are the only importers. Those calls exist to SATISFY a step-up gate, so if
 *     one of them ever answered with its own `403 STEP_UP_REQUIRED`, routing it back through {@link request}
 *     would call `runStepUpCeremony` from inside the ceremony that owns the in-flight promise
 *     it would then await, a deadlock;
 *   - the login-link calls in api/logins.ts, which catch that 403 themselves;
 *   - the silent onboarding tax-id copy (`onboarding/client/taxIdCopy.ts`,
 *     crossmarc/appcore#739), which must never put a prompt in front of anyone: it runs
 *     unasked, so a stale step-up means "not now", not "ask";
 *   - the automatic attempt of the kept-bank tidy-up (`components/KeptBringIn.tsx`), which
 *     deletes accounts and runs with no one having pressed anything, so it too must never put
 *     a prompt in front of anyone. The same component's Finish button, which a person pressed,
 *     uses the intercepting client.
 *
 * Everything else (redirect-on-suspension, the same `ApiError` shape, timeouts) behaves
 * identically to the intercepted client, including the `workspaceId` override.
 */
export function createRawApiClient(token: string) {
  return {
    get:  <T>(path: string, opts?: ScopedGetOptions) => rawRequest<T>(path, token, {}, bindWorkspace(opts?.workspaceId)),
    post: <T>(path: string, body: unknown, opts?: ScopedWriteOptions & PostOptions) =>
      rawRequest<T>(path, token, { method: 'POST', body: JSON.stringify(body), ...(opts?.signal ? { signal: opts.signal } : {}) }, bindWorkspace(opts?.workspaceId), opts?.timeoutMs),
  };
}
